import React, { useState, useContext } from "react";
import Carousel, { Alignment } from "nuka-carousel";
import { ButtonBase, IconButton } from "@mui/material";
import { GameContext, GameContextProps } from "../contexts";
import { Choice } from "../story";
import { CarouselCard } from "./CarouselCard";
import { EventCategory } from "../analytics";
import leftArrowIcon from "../assets/icons/deco-arrow-left.png";
import rightArrowIcon from "../assets/icons/deco-arrow-right.png";
import selectIcon from "../assets/icons/arrow-down-center.png";
import { hoverOverride } from "./helpers";

/**
 * Parameters expected by a ChoiceCarousel. 
 * @param {string[]} choices - the set of choices to be rendered by the carousel.
 * @param {function} choiceFunction - The function to be called when a choice is selected.
*/
interface CarouselProps {
    choices: Choice[];
    onChoiceSelected: (choice: Choice) => void;
    scrollId: string;
}

/**
 * The UI widget for displaying a set of choices as an animated carousel.
 * @param {CarouselProps} props - Parameters expected by the ChoiceCarousel.
*/
export const ChoiceCarousel = (props: CarouselProps) => {
    const { choices, onChoiceSelected, scrollId } = props;
    const initialSlideIndex = 1;
    const [focusedSlide, setFocus] = useState(initialSlideIndex);
    const [isChoiceSelected, setChoiceSelected] = useState(false);
    const { modifyEnergy, currentEnergy, analytics } = useContext(GameContext) as GameContextProps;

    const onChoiceClicked = (index: number) => {
        const currentChoice = choices[index];
        if (index !== focusedSlide) {
            setFocus(index);
            return;
        }

        if (isChoiceDisabled(currentChoice)) {
            return;
        }

        modifyEnergy(currentChoice.energyModifier);
        setChoiceSelected(true);
        analytics?.recordEvent(EventCategory.CHOICE, currentChoice.choiceText);
        onChoiceSelected(currentChoice);
    };

    const isChoiceDisabled = (choice: Choice) => {
        const energyModifier = choice.energyModifier;
        const notEnoughEnergy = currentEnergy + energyModifier < 0;
        return notEnoughEnergy;
    }

    const renderPreviousButton = (currentIndex: number) => {
        const newIndex = (currentIndex - 1) % choices.length;
        return (
            <div className="carousel-button">
                <IconButton onClick={() => onChoiceClicked(newIndex)} size="small" sx={hoverOverride}>
                    <img src={leftArrowIcon} alt="Previous"/>
                </IconButton>
            </div>
        );
    }
      
    const renderNextButton = (currentIndex: number) => {
        const newIndex = (currentIndex + 1) % choices.length;
        return (
            <div className="carousel-button">
                <IconButton onClick={() => onChoiceClicked(newIndex)} size="small" sx={hoverOverride}>
                    <img src={rightArrowIcon} alt="Next"/>
                </IconButton>
            </div>
        );
    }

    const renderSelectButton = () => {
        return (
            <div className="carousel-button-select">
                <IconButton onClick={() => onChoiceClicked(focusedSlide)} size="small" disabled={isChoiceSelected} sx={hoverOverride}>
                    <img src={selectIcon} alt="Next"/>
                </IconButton>
            </div>
        );
    }
    
    return (
        <div className="carousel-container">
            <Carousel
                slidesToShow={3}
                wrapAround={true}
                slideIndex={focusedSlide}
                cellAlign={Alignment.Center}
                animation="zoom"
                zoomScale={0.7}
                speed={1000}
                renderBottomLeftControls={({currentSlide}) => renderPreviousButton(currentSlide)}
                renderBottomRightControls={({currentSlide}) => renderNextButton(currentSlide)}
                renderCenterLeftControls={() => (<></>)}
                renderCenterRightControls={() => (<></>)}
                renderBottomCenterControls={renderSelectButton}
            >
                {choices.map((item, index) => {
                    return (
                        <ButtonBase data-testid={`choice${index}`} key={index} disabled={isChoiceSelected || isChoiceDisabled(item)} onClick={() => onChoiceClicked(index)}>
                            <CarouselCard scrollId={scrollId} content={item} isDisabled={isChoiceDisabled(item) || isChoiceSelected}/>
                        </ButtonBase>
                    );
                })}
            </Carousel>
        </div>
    );
}